import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	CardFooter,
} from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import {
	CheckCircle2,
	CreditCard,
	ExternalLink,
	XCircle,
	Crown,
	Zap,
	AlertTriangle,
	Sparkles,
	AtSign,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { usePlanInfo } from '@/hooks/usePlanInfo'
import { PricingCard } from '@/components/landing/PricingCard'
import { BillingToggle } from './PricingCard'
import { PricingFeatureItem } from '@/components/landing/PricingFeatureItem'
import { useTeamContext } from '@/context/TeamContext'
import { useProgramContext } from '@/context/ProgramContext'
import { isAccountOwner } from '@/lib/programPermissions'

import { useAuth } from '@/context/AuthContext'
import { getApiBaseUrl } from '@/lib/api'
import stripePromise from '@/lib/stripe'
import { useTranslation } from '@/hooks/useTranslation'
import {
	getPlanFeatures,
	usePricingData,
	calculateSavings,
	buildDefaultPlans,
	mergePricingData,
} from '@/lib/pricing'
import { BRAND_MENTION_ADDON_PRICE_MONTHLY } from '@/types/brandMentions'

// API URL using centralized function
const API_URL = getApiBaseUrl()

const BillingSettings = () => {
	const { t } = useTranslation()
	const { teamId, role: teamRole } = useTeamContext()
	const { isInvitedProgram } = useProgramContext()
	const authContext = useAuth()
	const { user } = authContext

	const planFeatures = getPlanFeatures(t)
	const { toast } = useToast()
	const [searchParams] = useSearchParams()
	const success = searchParams.get('success') === 'true'
	const cancelled = searchParams.get('cancelled') === 'true'
	const queryClient = useQueryClient()
	const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>(
		'yearly',
	)
	const [starterSeats, setStarterSeats] = useState<number>(1)
	const [growthSeats, setGrowthSeats] = useState<number>(1)
	const [brandMentionsAddonToggling, setBrandMentionsAddonToggling] =
		useState(false)

	const getAuthHeaders = async () => {
		const {
			data: { session },
		} = await supabase.auth.getSession()

		if (!session?.access_token) {
			throw new Error('You must be logged in to manage billing.')
		}

		return {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${session.access_token}`,
		}
	}

	const handleSeatChange = (planKey: 'starter' | 'growth', newSeats: number) => {
		if (planKey === 'starter') {
			setStarterSeats(newSeats)
		} else {
			setGrowthSeats(newSeats)
		}

		localStorage.setItem(`selected_${planKey}_seats`, newSeats.toString())
	}

	const calculatePlanPrice = (planKey: 'starter' | 'growth', seats: number) => {
		const plan = plans[planKey]
		const baseMonthlyPrice =
			typeof plan.monthlyPrice === 'number' ? plan.monthlyPrice : 0
		const baseYearlyPrice =
			typeof plan.yearlyPrice === 'number' ? plan.yearlyPrice : 0
		const additionalMonthlyPrice = plan.additionalMemberPriceMonthly ?? 0
		const additionalYearlyPrice =
			plan.additionalMemberPriceYearly ?? additionalMonthlyPrice * 12
		const additionalSeats = Math.max(0, seats - 1)

		if (billingPeriod === 'monthly') {
			return baseMonthlyPrice + additionalSeats * additionalMonthlyPrice
		}

		return Math.round(baseYearlyPrice / 12) + additionalSeats * additionalMonthlyPrice
	}

	const calculateTotalYearlyPrice = (
		planKey: 'starter' | 'growth',
		seats: number
	) => {
		const plan = plans[planKey]
		const baseYearlyPrice =
			typeof plan.yearlyPrice === 'number' ? plan.yearlyPrice : 0
		const additionalYearlyPrice =
			plan.additionalMemberPriceYearly ??
			(plan.additionalMemberPriceMonthly ?? 0) * 12
		const additionalSeats = Math.max(0, seats - 1)
		return baseYearlyPrice + additionalSeats * additionalYearlyPrice
	}

	const SeatSelector = ({
		planKey,
		seats,
	}: {
		planKey: 'starter' | 'growth'
		seats: number
	}) => (
		<div className="flex flex-col items-center gap-2">
			<p className="text-sm font-medium text-foreground">Team members</p>
			<div className="flex items-center justify-center gap-4">
				<button
					onClick={() =>
						handleSeatChange(planKey, Math.max(1, seats - 1))
					}
					className="w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center text-lg font-semibold transition-colors"
					aria-label="Decrease seats">
					-
				</button>
				<div className="w-20 h-10 rounded-lg border-2 flex items-center justify-center text-lg font-semibold">
					{seats}
				</div>
				<button
					onClick={() => handleSeatChange(planKey, seats + 1)}
					className="w-10 h-10 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center text-lg font-semibold transition-colors"
					aria-label="Increase seats">
					+
				</button>
			</div>
			<p className="text-xs text-muted-foreground">
				{t('pricing.shared.additionalMemberPrice')}
			</p>
		</div>
	)

	// Get plan info
	const {
		data: planInfo,
		isLoading: planLoading,
		error: planError,
	} = usePlanInfo()
	const isPremium = planInfo?.isPremium ?? false
	const hasLinkedBillingCustomer = Boolean(planInfo?.subscription.customerId)
	const showCurrentPlanState = Boolean(isPremium && planInfo?.planId !== 'free')

	// Get seats count from team
	const { data: teamData } = useQuery({
		queryKey: ['teamSeats', teamId],
		queryFn: async () => {
			if (!teamId) return null
			const { data, error } = await supabase
				.from('teams')
				.select('seats_count')
				.eq('id', teamId)
				.single()
			if (error) throw error
			return data
		},
		enabled: !!teamId,
	})

	const seatsCount = teamData?.seats_count || 1

	const { data: aiAddonStatus, isLoading: aiAddonLoading, refetch: refetchAiAddon } =
		useQuery<{
			enabled: boolean
			canManage: boolean
			hasActiveSubscription: boolean
			configured: boolean
		}>({
			queryKey: ['aiReplyAddonStatus', teamId],
			queryFn: async () => {
				if (!teamId) {
					return {
						enabled: false,
						canManage: false,
						hasActiveSubscription: false,
						configured: false,
					}
				}
				const response = await fetch(
					`${API_URL}/api/stripe/ai-reply-assistant/status?teamId=${teamId}`,
					{
						headers: await getAuthHeaders(),
					}
				)
				if (!response.ok) {
					throw new Error('Failed to load AI addon status')
				}
				return response.json()
			},
			enabled: !!teamId && showCurrentPlanState && hasLinkedBillingCustomer,
		})

	const {
		data: brandMentionsAddonStatus,
		isLoading: brandMentionsAddonLoading,
		refetch: refetchBrandMentionsAddon,
	} = useQuery<{
		enabled: boolean
		canManage: boolean
		hasActiveSubscription: boolean
		eligiblePlan: boolean
		configured: boolean
		priceMonthly: number
	}>({
		queryKey: ['brandMentionsAddonStatus', teamId],
		queryFn: async () => {
			if (!teamId) {
				return {
					enabled: false,
					canManage: false,
					hasActiveSubscription: false,
					eligiblePlan: false,
					configured: false,
					priceMonthly: BRAND_MENTION_ADDON_PRICE_MONTHLY,
				}
			}
			const response = await fetch(
				`${API_URL}/api/stripe/brand-mentions/status?teamId=${teamId}`,
				{
					headers: await getAuthHeaders(),
				}
			)
			if (!response.ok) {
				throw new Error('Failed to load brand mentions addon status')
			}
			return response.json()
		},
		enabled: !!teamId && showCurrentPlanState && hasLinkedBillingCustomer,
	})

	const handleAiAddonToggle = async (enable: boolean) => {
		if (!teamId) return
		try {
			const response = await fetch(
				`${API_URL}/api/stripe/ai-reply-assistant/${enable ? 'enable' : 'disable'}`,
				{
					method: 'POST',
					headers: await getAuthHeaders(),
					body: JSON.stringify({ teamId }),
				}
			)

			const payload = await response.json().catch(() => ({}))
			if (!response.ok) {
				throw new Error(payload?.error || 'Failed to update addon')
			}

			toast({
				title: enable ? 'AI Reply Assistant enabled' : 'AI Reply Assistant disabled',
				description: enable
					? 'The add-on is now active for your team.'
					: 'The add-on has been removed from your subscription.',
			})

			refetchAiAddon()
		} catch (error) {
			toast({
				variant: 'destructive',
				title: t('common.error'),
				description:
					error instanceof Error ? error.message : 'Failed to update add-on',
			})
		}
	}

	const handleBrandMentionsAddonToggle = async (enable: boolean) => {
		if (!teamId || brandMentionsAddonToggling) return
		setBrandMentionsAddonToggling(true)
		try {
			const response = await fetch(
				`${API_URL}/api/stripe/brand-mentions/${enable ? 'enable' : 'disable'}`,
				{
					method: 'POST',
					headers: await getAuthHeaders(),
					body: JSON.stringify({ teamId }),
				}
			)

			const payload = await response.json().catch(() => ({}))
			if (!response.ok) {
				throw new Error(payload?.error || 'Failed to update addon')
			}

			toast({
				title: enable
					? t('billingSettings.brandMentionsAddonEnabled')
					: t('billingSettings.brandMentionsAddonDisabled'),
				description: enable
					? t('billingSettings.brandMentionsAddonEnabledDescription')
					: t('billingSettings.brandMentionsAddonDisabledDescription'),
			})

			await refetchBrandMentionsAddon()
		} catch (error) {
			toast({
				variant: 'destructive',
				title: t('common.error'),
				description:
					error instanceof Error ? error.message : 'Failed to update add-on',
			})
		} finally {
			setBrandMentionsAddonToggling(false)
		}
	}

	// Get pricing data from Stripe
	const {
		data: pricingData,
		isLoading: pricingLoading,
		error: pricingError,
	} = usePricingData()

	// Build default plans and merge with API data
	const defaultPlans = buildDefaultPlans(t, planFeatures)
	const plans = mergePricingData(defaultPlans, pricingData)

	const freePlan = plans.free
	const starterPlan = plans.starter
	const growthPlan = plans.growth
	const enterprisePlan = plans.enterprise
	const currentPlanFeatures =
		planInfo?.planId === 'growth' ? planFeatures.growth : planFeatures.starter

	const planName = planInfo?.planName ?? t('billingSettings.freeTier')
	const subscriptionStatus = planInfo?.subscription.status
	const statusDisplay =
		subscriptionStatus === 'active' || subscriptionStatus === 'trialing'
			? t('billingSettings.active')
			: subscriptionStatus ||
			(isPremium ? t('billingSettings.active') : t('billingSettings.free'))

	useEffect(() => {
		if (success) {
			toast({
				title: t('billingSettings.paymentSuccessful'),
				description: t('billingSettings.paymentSuccessfulDescription'),
			})
			// Refetch plan info after successful payment
			queryClient.invalidateQueries({ queryKey: ['planInfo', teamId] })
		} else if (cancelled) {
			toast({
				variant: 'destructive',
				title: t('billingSettings.paymentCancelled'),
				description: t('billingSettings.paymentCancelledDescription'),
			})
		}
	}, [success, cancelled, toast, queryClient, teamId, t])

	const redirectToStripePortal = async () => {
		try {
			if (!planInfo?.subscription.customerId) {
				toast({
					variant: 'destructive',
					title: t('common.error'),
					description: t('billingSettings.noCustomerFound'),
				})
				return
			}

			const response = await fetch(`${API_URL}/api/stripe/create-portal-session`, {
				method: 'POST',
				headers: await getAuthHeaders(),
				body: JSON.stringify({
					teamId,
					customerId: planInfo.subscription.customerId,
				}),
			})

			if (!response.ok) {
				const errorData = await response.json()
				console.error('Portal session error:', errorData)

				// Check for specific Stripe errors
				if (errorData.error && errorData.error.includes('No such customer')) {
					toast({
						variant: 'destructive',
						title: t('billingSettings.invalidCustomer'),
						description: t('billingSettings.invalidCustomerDescription'),
					})

					// Reset the invalid customer ID in the database
					try {
						await supabase
							.from('teams')
							.update({
								stripe_customer_id: null,
								stripe_subscription_id: null,
								subscription_status: null,
							})
							.eq('id', teamId)

						// Refresh the page to update the UI
						window.location.reload()
					} catch (dbError) {
						console.error('Failed to reset customer data:', dbError)
					}

					return
				}

				throw new Error(
					errorData.error || t('billingSettings.failedToCreatePortalSession'),
				)
			}

			const { url } = await response.json()
			window.location.href = url
		} catch (error) {
			console.error('Error redirecting to portal:', error)
			toast({
				variant: 'destructive',
				title: t('common.error'),
				description: t('billingSettings.failedToRedirect'),
			})
		}
	}

	const createCheckoutSession = async (
		planKey: 'starter' | 'growth',
		seats: number
	) => {
		if (!teamId || !user?.email) {
			toast({
				variant: 'destructive',
				title: t('common.error'),
				description: t('billingSettings.missingRequiredInfo'),
			})
			return
		}

		try {
			const stripe = await stripePromise
			if (!stripe) throw new Error('Stripe failed to load')
			const refindieMetadata = (window as any).refindie_metadata;
			const selectedPlan = plans[planKey]
			const selectedPriceId =
				billingPeriod === 'monthly'
					? selectedPlan.monthlyPriceId
					: selectedPlan.yearlyPriceId

			if (!selectedPriceId) {
				throw new Error(`Stripe price is not configured for the ${planKey} plan`)
			}

			const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
				method: 'POST',
				headers: await getAuthHeaders(),
				body: JSON.stringify({
					teamId,
					priceId: selectedPriceId,
					email: user.email,
					metadata: refindieMetadata,
					seats,
					billingPeriod: billingPeriod,
				}),
			})

			if (!response.ok) {
				const errorData = await response.json()
				console.error('Checkout session error:', errorData)

				// Handle specific error for existing subscription
				if (errorData.details === 'use_portal_to_manage') {
					toast({
						variant: 'destructive',
						title: t('billingSettings.alreadySubscribed'),
						description: t('billingSettings.usePortalToManage'),
					})
					// Refresh the page to show correct state
					window.location.reload()
					return
				}

				throw new Error(
					errorData.error || t('billingSettings.failedToCreatePortalSession'),
				)
			}

			const { sessionId } = await response.json()
			const result = await stripe.redirectToCheckout({ sessionId })

			if (result.error) {
				throw new Error(result.error.message)
			}
		} catch (error) {
			console.error('Error redirecting to Stripe:', error)
			toast({
				variant: 'destructive',
				title: t('common.error'),
				description:
					error instanceof Error
						? error.message
						: t('billingSettings.failedToStartSubscription'),
			})
		}
	}

	// Calculate savings percentage for yearly billing (needed for toggle)
	const savingsPercentage = calculateSavings(
		typeof starterPlan.monthlyPrice === 'number' ? starterPlan.monthlyPrice : 0,
		typeof starterPlan.yearlyPrice === 'number' ? starterPlan.yearlyPrice : 0,
	)

	if (planLoading || pricingLoading) {
		return (
			<div className="space-y-6">
				{/* Billing toggle - always visible like landing page */}
				<BillingToggle
					billingPeriod={billingPeriod}
					onChange={setBillingPeriod}
					className="mb-4"
					savingsPercentage={savingsPercentage}
				/>

				{/* Success alert skeleton (only shown if needed) */}
				{success && (
					<Alert className="bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700">
						<CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
						<AlertTitle className="text-green-800 dark:text-green-300">
							{t('billingSettings.paymentSuccessful')}
						</AlertTitle>
						<AlertDescription className="text-green-700 dark:text-green-400">
							{t('billingSettings.subscriptionActivated')}
						</AlertDescription>
					</Alert>
				)}

				<div className="mt-7 grid w-full grid-cols-1 lg:grid-cols-3 gap-5">
					<Skeleton className="h-[500px] rounded-2xl" />
					<Skeleton className="h-[500px] rounded-2xl" />
					<Skeleton className="h-[500px] rounded-2xl" />
				</div>
			</div>
		)
	}

	if (planError) {
		return (
			<Alert variant="destructive">
				<AlertTriangle className="h-4 w-4" />
				<AlertTitle>{t('common.error')}</AlertTitle>
				<AlertDescription>
					{t('billingSettings.couldNotLoadBilling')}
				</AlertDescription>
			</Alert>
		)
	}

	if (pricingError) {
		// Continue with fallback values, no need to show error to user
	}

	// Show restricted access message for invited users (non-admin/owner)
	if (isInvitedProgram || !isAccountOwner(teamRole)) {
		return (
			<div className="space-y-6">
				<Card>
					<CardHeader>
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-muted">
								<CreditCard className="h-4 w-4 text-muted-foreground" />
							</div>
							<div>
								<CardTitle>
									{t('billingSettings.billingSettingsTitle')}
								</CardTitle>
								<CardDescription>
									{isInvitedProgram
										? t('billingSettings.guestMember')
										: t('billingSettings.administrativeAccessRequired')}
								</CardDescription>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						<div className="rounded-lg border bg-muted/50 p-4">
							<p className="text-sm text-muted-foreground">
								{t('billingSettings.billingManagedByAdmins')}
								{isPremium && ' ' + t('billingSettings.teamOnPaidPlan')}
							</p>
						</div>
					</CardContent>
				</Card>
			</div>
		)
	}

	return (
		<div className="space-y-8  ">
			{/* Billing Toggle - always visible like landing page */}
			<BillingToggle
				billingPeriod={billingPeriod}
				onChange={setBillingPeriod}
				className="mb-4"
				savingsPercentage={savingsPercentage}
			/>

			{/* Success Alert */}
			{success && (
				<Alert
					variant="default"
					className="bg-green-50 border-green-200 dark:bg-green-900/30 dark:border-green-700">
					<CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
					<AlertTitle className="text-green-800 dark:text-green-300">
						{t('billingSettings.paymentSuccessful')}
					</AlertTitle>
					<AlertDescription className="text-green-700 dark:text-green-400">
						{t('billingSettings.subscriptionActive')}
					</AlertDescription>
				</Alert>
			)}
			{/* Cancelled Alert */}
			{cancelled && (
				<Alert variant="destructive">
					<XCircle className="h-4 w-4" />
					<AlertTitle>{t('billingSettings.paymentCancelled')}</AlertTitle>
					<AlertDescription>
						{t('billingSettings.subscriptionCancelledDescription', {
							planName,
						})}
					</AlertDescription>
				</Alert>
			)}

			{/* Current Plan Status */}
			<div className="flex flex-col items-center gap-4">
				<div className="flex flex-col gap-1.5 items-center">
					<div className="flex items-center gap-2">
						<span className="font-medium text-primary text-center text-sm">
							{t('billingSettings.currentPlan')}:
						</span>
						<h2 className="font-heading text-2xl font-semibold tracking-tight text-balance sm:text-3xl">
							{planName}
						</h2>
					</div>
				</div>
				{showCurrentPlanState && (
					<div className="text-center">
						<Badge variant="default" className="text-xs">
							{statusDisplay}
						</Badge>
					</div>
				)}
			</div>

			{/* Pro User Subscription Management */}
			{showCurrentPlanState ? (
				<div className="space-y-4 max-w-2xl mx-auto">
					<Card className="rounded-xl shadow-sm">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Crown className="h-5 w-5 text-primary" />
								{t('billingSettings.currentPlanActive', { planName })}
							</CardTitle>
							<CardDescription>
								{t('billingSettings.accessToCurrentPlanFeatures')}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ul className="grid grid-cols-1 md:grid-cols-2 gap-2 my-4">
								{currentPlanFeatures.map((feature, i) => (
									<PricingFeatureItem key={i} text={feature} />
								))}
								{isPremium && (
									<PricingFeatureItem
										text={`${seatsCount} ${seatsCount === 1 ? t('billingSettings.seat') : t('billingSettings.seats')}`}
									/>
								)}
							</ul>
						</CardContent>
						<CardFooter>
							{hasLinkedBillingCustomer ? (
								<Button
									size="default"
									variant="outline"
									className="font-medium w-full"
									onClick={redirectToStripePortal}>
									{t('billingSettings.manageSubscription')}
									<ExternalLink className="h-4 w-4 ml-1.5" />
								</Button>
							) : (
								<div className="w-full rounded-lg border border-dashed bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
									<p className="font-medium text-foreground">
										{t('billingSettings.billingLinkUnavailable')}
									</p>
									<p className="mt-1">
										{t('billingSettings.billingLinkUnavailableDescription')}
									</p>
								</div>
							)}
						</CardFooter>
					</Card>

					{hasLinkedBillingCustomer && (
						<Card className="rounded-xl shadow-sm border-dashed">
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Sparkles className="h-5 w-5 text-primary" />
									AI Reply Assistant
								</CardTitle>
								<CardDescription>
									Generate ready-to-send mailbox replies from previous answers and uploaded documentation.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-3">
								<div className="flex items-center justify-between text-sm">
									<span className="text-muted-foreground">Add-on price</span>
									<span className="font-medium">+$15 / team / month</span>
								</div>
								<div className="flex items-center justify-between text-sm">
									<span className="text-muted-foreground">Status</span>
									<Badge variant={aiAddonStatus?.enabled ? 'default' : 'secondary'}>
										{aiAddonStatus?.enabled ? 'Enabled' : 'Disabled'}
									</Badge>
								</div>
							</CardContent>
							<CardFooter>
								<Button
									size="default"
									variant={aiAddonStatus?.enabled ? 'outline' : 'default'}
									className="w-full"
									disabled={
										aiAddonLoading ||
										!aiAddonStatus?.configured ||
										!aiAddonStatus?.canManage ||
										!aiAddonStatus?.hasActiveSubscription
									}
									onClick={() => handleAiAddonToggle(!aiAddonStatus?.enabled)}
								>
									{aiAddonStatus?.enabled ? 'Disable Add-on' : 'Enable Add-on'}
								</Button>
							</CardFooter>
						</Card>
					)}

					{hasLinkedBillingCustomer && brandMentionsAddonStatus?.eligiblePlan && (
						<Card className="rounded-xl shadow-sm border-dashed">
							<CardContent className="flex flex-row items-start gap-4 p-6">
								<div className="mt-0.5 flex-shrink-0">
									<AtSign className="h-5 w-5 text-primary" />
								</div>
								<div className="min-w-0 flex-1 space-y-1">
									<Label
										htmlFor="brand-mentions-addon-switch"
										className="cursor-pointer text-base font-semibold">
										{t('billingSettings.brandMentionsAddonTitle')}
									</Label>
									<p className="text-sm text-muted-foreground">
										{t('billingSettings.brandMentionsAddonDescription')}
									</p>
									<p className="pt-1 text-sm font-medium">
										{t('billingSettings.brandMentionsAddonPrice')}
									</p>
								</div>
								<Switch
									id="brand-mentions-addon-switch"
									checked={Boolean(brandMentionsAddonStatus?.enabled)}
									onCheckedChange={(checked) =>
										handleBrandMentionsAddonToggle(checked)
									}
									disabled={
										brandMentionsAddonLoading ||
										brandMentionsAddonToggling ||
										!brandMentionsAddonStatus?.configured ||
										!brandMentionsAddonStatus?.canManage ||
										!brandMentionsAddonStatus?.hasActiveSubscription ||
										!brandMentionsAddonStatus?.eligiblePlan
									}
									aria-label={t('billingSettings.brandMentionsAddonTitle')}
								/>
							</CardContent>
						</Card>
					)}
				</div>
			) : (
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<PricingCard
							name={freePlan.name}
							isMostPopular={false}
							features={freePlan.features}
							price={0}
							isLoading={pricingLoading}
							hideButton={true}
						/>
						<PricingCard
							name={starterPlan.name}
							isMostPopular={false}
							features={starterPlan.features}
							price={calculatePlanPrice('starter', starterSeats)}
							isLoading={pricingLoading}
							priceSubtext={
								billingPeriod === 'yearly'
									? t('pricingCard.billedYearly', {
										price: calculateTotalYearlyPrice('starter', starterSeats),
									})
									: undefined
							}
							seatSelector={<SeatSelector planKey="starter" seats={starterSeats} />}
							customButton={
								<Button
									size="lg"
									className="font-semibold text-lg py-4 w-full"
									onClick={() => createCheckoutSession('starter', starterSeats)}>
									<Zap className="h-4 w-4 mr-1.5" />
									{t('pricingCard.getStarted')}
								</Button>
							}
						/>
						<PricingCard
							name={growthPlan.name}
							isMostPopular={true}
							features={growthPlan.features}
							price={calculatePlanPrice('growth', growthSeats)}
							isLoading={pricingLoading}
							priceSubtext={
								billingPeriod === 'yearly'
									? t('pricingCard.billedYearly', {
										price: calculateTotalYearlyPrice('growth', growthSeats),
									})
									: undefined
							}
							seatSelector={<SeatSelector planKey="growth" seats={growthSeats} />}
							customButton={
								<Button
									size="lg"
									className="font-semibold text-lg py-4 w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 shadow-lg"
									onClick={() => createCheckoutSession('growth', growthSeats)}>
									<Zap className="h-4 w-4 mr-1.5" />
									{t('pricingCard.getGrowth')}
								</Button>
							}
						/>
					</div>

					<div className="rounded-xl border border-dashed px-4 py-4 sm:px-5">
						<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
							<div className="space-y-1">
								<p className="text-sm font-medium">
									{t('pricing.addons.brandMentionsTitle')}
								</p>
								<p className="text-sm text-muted-foreground">
									{t('pricing.addons.brandMentionsDescription')}
								</p>
							</div>
							<p className="shrink-0 text-sm font-semibold">
								{t('pricing.addons.brandMentionsPrice')}
							</p>
						</div>
					</div>

					<Card className="rounded-xl shadow-sm">
						<CardHeader>
							<CardTitle>{t('pricing.enterprise.ctaTitle')}</CardTitle>
							<CardDescription>
								{t('pricing.enterprise.description')}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">
								{enterprisePlan.features.map((feature, i) => (
									<PricingFeatureItem key={i} text={feature} />
								))}
							</ul>
						</CardContent>
						<CardFooter className="justify-end">
							<Button
								size="lg"
								variant="outline"
								className="font-semibold text-lg py-4"
								asChild>
								<a href="mailto:contact@feedbask.com">
									{t('pricingCard.contactUs')}
								</a>
							</Button>
						</CardFooter>
					</Card>
				</div>
			)}
		</div>
	)
}

export default BillingSettings
